
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export const useRobustWebsiteSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['website-settings'],
    queryFn: async () => {
      console.log('Fetching robust website settings...');
      const { data, error } = await supabase
        .from('website_settings')
        .select('key, value');
      
      if (error) {
        console.error('Error fetching robust settings:', error);
        throw error;
      }
      
      const settingsObj = (data || []).reduce((acc: Record<string, any>, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {});
      
      // Ensure site_name defaults to TerraGamers if not set
      if (!settingsObj.site_name) {
        settingsObj.site_name = 'TerraGamers';
      }
      
      console.log('Processed robust settings:', settingsObj);
      return settingsObj;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 2,
    retryDelay: 1000,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      console.log('Updating robust settings:', settingsToUpdate);
      
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        return supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
      });
      
      const results = await Promise.all(updatePromises);
      
      const failedUpdates = results.filter(result => result.error);
      if (failedUpdates.length > 0) {
        console.error('Failed robust updates:', failedUpdates);
        throw new Error(`Failed to update ${failedUpdates.length} settings`);
      }
      
      return { settingsToUpdate, results };
    },
    onSuccess: async (data) => {
      const { settingsToUpdate } = data;
      console.log('Robust settings updated successfully:', settingsToUpdate);
      
      // Simple invalidation and refetch
      await queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      await queryClient.refetchQueries({ queryKey: ['website-settings'] });
      
      toast.success('Settings updated successfully');
    },
    onError: (error: any) => {
      console.error('Robust settings update error:', error);
      toast.error('Failed to update settings. Please try again.');
    },
  });

  return {
    settings,
    isLoading,
    isError,
    error,
    refetch,
    updateSettings: updateSettingsMutation.mutate,
    isUpdating: updateSettingsMutation.isPending,
  };
};
